<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { buildTimeSlots, groupSlotsByPeriod } from './helpers';

const props = defineProps({
  modelValue: { type: String, default: null },
  accent: {
    type: String,
    default: 'blue',
    validator: value => ['blue', 'teal'].includes(value),
  },
  startHour: { type: Number, default: 8 },
  endHour: { type: Number, default: 20 },
  stepMinutes: { type: Number, default: 30 },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const selectedClass = computed(() =>
  props.accent === 'teal' ? 'bg-n-teal-9 text-white' : 'bg-n-blue-9 text-white'
);

const periods = computed(() => {
  const grouped = groupSlotsByPeriod(
    buildTimeSlots(props.startHour, props.endHour, props.stepMinutes)
  );
  return [
    {
      key: 'morning',
      label: t('TIME_SLOT_PICKER.MORNING'),
      slots: grouped.morning,
    },
    {
      key: 'afternoon',
      label: t('TIME_SLOT_PICKER.AFTERNOON'),
      slots: grouped.afternoon,
    },
    {
      key: 'evening',
      label: t('TIME_SLOT_PICKER.EVENING'),
      slots: grouped.evening,
    },
  ].filter(period => period.slots.length);
});

const selectSlot = slot => {
  emit('update:modelValue', slot);
};

const slotClass = slot => [
  'h-7 rounded-full text-[11.5px] font-bold tabular-nums',
  slot === props.modelValue
    ? selectedClass.value
    : 'hover:bg-n-alpha-2 text-n-slate-12',
];
</script>

<template>
  <div
    class="max-h-44 overflow-y-auto rounded-2xl border border-n-weak bg-n-solid-2 p-3"
  >
    <div
      v-for="period in periods"
      :key="period.key"
      class="mb-2 last:mb-0"
      :data-period="period.key"
    >
      <div class="mb-1 text-[10.5px] font-bold uppercase text-n-slate-10">
        {{ period.label }}
      </div>
      <div class="grid grid-cols-5 gap-1">
        <button
          v-for="slot in period.slots"
          :key="slot"
          type="button"
          :data-slot="slot"
          :aria-pressed="slot === modelValue"
          :class="slotClass(slot)"
          @click="selectSlot(slot)"
        >
          {{ slot }}
        </button>
      </div>
    </div>
  </div>
</template>
